import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchFolioSummary,
  addFolioItem,
  removeFolioItem,
  recordPayment,
  generateAndFetchBill,
  type AddFolioItemPayload,
  type RecordPaymentPayload,
  type GuestInvoice,
} from '@/services/api/folio.api';

export function useFolioSummary(bookingId: string | null | undefined) {
  return useQuery({
    queryKey: ['folio', bookingId],
    queryFn: () => fetchFolioSummary(bookingId!),
    enabled: !!bookingId,
    staleTime: 15_000,
  });
}

export function useAddFolioItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, payload }: { bookingId: string; payload: AddFolioItemPayload }) =>
      addFolioItem(bookingId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['folio', variables.bookingId] });
    },
  });
}

export function useRemoveFolioItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId }: { itemId: string; bookingId: string }) =>
      removeFolioItem(itemId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['folio', variables.bookingId] });
    },
  });
}

export function useRecordPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: RecordPaymentPayload) => recordPayment(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['folio', variables.bookingId] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}

export interface BillPreview {
  blobUrl: string;
  invoice: GuestInvoice;
}

/**
 * Generates a guest invoice and returns a blob URL for in-page preview.
 * Closing the modal revokes the blob URL (memory) but keeps the last invoice
 * metadata so the Bill No. banner and "Re-open Bill" button persist.
 */
export function usePrintBill() {
  const [isPrinting, setIsPrinting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<BillPreview | null>(null);
  // Keeps invoice data after the modal closes so the banner stays visible
  const [lastInvoice, setLastInvoice] = useState<GuestInvoice | null>(null);

  const openBill = useCallback(async (bookingId: string) => {
    setIsPrinting(true);
    setError(null);
    try {
      const result = await generateAndFetchBill(bookingId);
      setLastInvoice(result.invoice);
      setPreview(result);
    } catch (e) {
      setError((e as Error)?.message ?? 'Failed to generate invoice');
    } finally {
      setIsPrinting(false);
    }
  }, []);

  const closePreview = useCallback(() => {
    if (preview?.blobUrl) URL.revokeObjectURL(preview.blobUrl);
    setPreview(null); // close modal + free blob, but lastInvoice stays
    setError(null);
  }, [preview]);

  return { openBill, isPrinting, error, preview, lastInvoice, closePreview };
}
